import { Signal } from '@angular/core';
import { User } from '@tourfold/libraries/tf-api-client-public-v2';
import { providePageContext } from '@tourfold/shared-kernels/page-context';

const MAX_VISIBLE_USERS = 25;

export interface UsersAiPageContextSignals {
  readonly users: Signal<User[]>;
  readonly searchQuery: Signal<string>;
  readonly sortField: Signal<string | null>;
  readonly sortOrder: Signal<number>;
  readonly originalUser: Signal<User | null>;
  readonly isEditDialogVisible: Signal<boolean>;
}

/**
 * Registers AI assistant page context for the users tab of /settings/access.
 *
 * - `entity` = the user being edited (dialog open). Create dialog leaves
 *   `entity` undefined (no specific user in focus yet).
 * - `visible_rows` = first 25 users so the assistant can refer to them by
 *   email without re-fetching.
 * - `filters` = search query and sort.
 *
 * See frontend/AGENTS.md → "AI Assistant Page Context" for the field rules.
 */
export function provideUsersAiPageContext(state: UsersAiPageContextSignals): void {
  providePageContext(() => {
    const editing = state.isEditDialogVisible() ? state.originalUser() : null;
    const sortField = state.sortField();
    const sortOrder = state.sortOrder();
    const search = state.searchQuery().trim();

    return {
      entity: editing
        ? {
            type: 'user',
            id: editing.id,
            label: editing.email,
          }
        : undefined,
      visible_rows: state
        .users()
        .slice(0, MAX_VISIBLE_USERS)
        .map((user) => ({
          type: 'user',
          id: user.id,
          label: user.email,
        })),
      filters: {
        ...(search.length > 0 && { search }),
        ...(sortField &&
          sortOrder !== 0 && {
            sort: `${sortField}:${sortOrder === 1 ? 'asc' : 'desc'}`,
          }),
      },
    };
  });
}
